import { flushSync } from 'react-dom';

/**
 * Animated screen changes, on the View Transitions API.
 *
 * The browser snapshots the page, the update runs, and the two states are
 * animated between (CSS in styles/app.css). One element on each side may be
 * named `book-art`: the cover you tapped on the way in, the player's artwork on
 * the way out — so the cover flies between the shelf and the player rather than
 * the screen merely cross-fading.
 *
 * Where the API is missing, motion is reduced or the page is hidden, updates
 * simply apply.
 */

const ART = 'book-art';

/** The cover element a tap came from, captured just before navigating. */
let artSource: HTMLElement | null = null;

export function setArtSource(element: HTMLElement | null): void {
  artSource = element;
}

export function canAnimate(): boolean {
  return (
    typeof document !== 'undefined' &&
    'startViewTransition' in document &&
    document.visibilityState === 'visible' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Runs `update` as an animated screen change. `bookId` names the book whose
 * cover should fly, if it is on screen at both ends.
 */
export function screenTransition(update: () => void, bookId: string | null): void {
  const source = artSource;
  artSource = null;

  if (!canAnimate()) {
    update();
    return;
  }

  // Exactly one element per side may carry the name, or the browser abandons
  // the whole transition.
  clearArt();
  if (source?.isConnected && !document.querySelector('.player__art')) {
    source.style.viewTransitionName = ART;
  }

  const transition = document.startViewTransition(() => {
    flushSync(update);
    if (source) source.style.viewTransitionName = '';
    // Arriving on the player, its artwork is already named. Arriving anywhere
    // else, the book's first cover on screen catches it.
    if (bookId && !document.querySelector('.player__art')) {
      const target = firstVisibleArt(bookId);
      if (target) target.style.viewTransitionName = ART;
    }
  });

  settle(transition, clearArt);
}

/**
 * A transition is skipped — its promises rejecting — whenever another starts
 * before it finishes, or the page is hidden. The update still runs, so none of
 * that is an error; it only needs catching, and the clean-up still doing.
 */
function settle(transition: ViewTransition, cleanup: () => void): void {
  transition.ready.catch(() => undefined);
  transition.updateCallbackDone.catch(() => undefined);
  transition.finished.then(cleanup, cleanup);
}

function firstVisibleArt(bookId: string): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>('[data-art]')) {
    if (el.dataset.art !== bookId) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.bottom > 0 && r.top < window.innerHeight) return el;
  }
  return null;
}

function clearArt(): void {
  for (const el of document.querySelectorAll<HTMLElement>('[data-art]')) {
    el.style.viewTransitionName = '';
  }
}

/**
 * Switches theme with the new one spreading in a circle from the point that
 * was pressed.
 */
export function themeTransition(update: () => void, x: number, y: number): void {
  if (!canAnimate()) {
    update();
    return;
  }
  const root = document.documentElement;
  root.classList.add('theme-switching');
  const transition = document.startViewTransition(() => flushSync(update));
  const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));

  transition.ready
    .then(() => {
      root.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        {
          duration: 620,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          pseudoElement: '::view-transition-new(root)',
        },
      );
    })
    .catch(() => undefined);
  settle(transition, () => root.classList.remove('theme-switching'));
}
